import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Building2, FileText, CheckCircle2, Download,
  Search, ArrowRight, ArrowLeft, Loader2, LogOut,
  ShieldCheck, RotateCcw
} from "lucide-react";
import { authAPI, gstAPI, venueAPI, templateAPI } from "@/lib/api";
import { generateAgreementPDF } from "@/lib/pdfGenerator";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_venue-onboard/artifacts/der85h7h_WhatsApp%20Image%202026-04-24%20at%2011.33.28.jpeg";

const STEPS = [
  { id: "gst", label: "GST Details", icon: Building2 },
  { id: "agreement", label: "Agreement", icon: FileText },
  { id: "complete", label: "Complete", icon: CheckCircle2 },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("bmm_user") || "{}");
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // GST form state
  const [gstRegistered, setGstRegistered] = useState(null);
  const [gstin, setGstin] = useState("");
  const [gstLoading, setGstLoading] = useState(false);
  const [venueData, setVenueData] = useState({
    legal_name: "", trade_name: "", business_address: "",
    venue_name: "", venue_address: "",
    capacity: "", additional_facilities: "",
    signatory_name: "", signatory_designation: "", signatory_email: "",
    signatory_phone: user.phone || ""
  });

  // Agreement state
  const [agreementContent, setAgreementContent] = useState("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const agreementRef = useRef(null);

  // Signature OTP state
  const [showSignatureDialog, setShowSignatureDialog] = useState(false);
  const [signatureOtp, setSignatureOtp] = useState("");
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureCountdown, setSignatureCountdown] = useState(0);

  // Execution state
  const [executionTimestamp, setExecutionTimestamp] = useState(null);

  // Check if user already has a venue
  useEffect(() => {
    const checkExistingVenue = async () => {
      try {
        const res = await venueAPI.getMyVenue();
        if (res.data.venue) {
          const venue = res.data.venue;
          if (venue.status === "executed") {
            setExecutionTimestamp(venue.executed_at);
            setVenueData({
              legal_name: venue.legal_name || "",
              trade_name: venue.trade_name || "",
              business_address: venue.business_address || "",
              venue_name: venue.venue_name || "",
              venue_address: venue.venue_address || "",
              capacity: venue.capacity || "",
              additional_facilities: venue.additional_facilities || "",
              signatory_name: venue.signatory_name || "",
              signatory_designation: venue.signatory_designation || "",
              signatory_email: venue.signatory_email || "",
              signatory_phone: venue.signatory_phone || ""
            });
            setCurrentStep(2);
          } else if (venue.status === "submitted") {
            setVenueData({
              legal_name: venue.legal_name || "",
              trade_name: venue.trade_name || "",
              business_address: venue.business_address || "",
              venue_name: venue.venue_name || "",
              venue_address: venue.venue_address || "",
              capacity: venue.capacity || "",
              additional_facilities: venue.additional_facilities || "",
              signatory_name: venue.signatory_name || "",
              signatory_designation: venue.signatory_designation || "",
              signatory_email: venue.signatory_email || "",
              signatory_phone: venue.signatory_phone || ""
            });
            setGstRegistered(venue.gst_registered);
            setGstin(venue.gstin || "");
            setCurrentStep(1);
          } else {
            // Draft - restore data
            setVenueData({
              legal_name: venue.legal_name || "",
              trade_name: venue.trade_name || "",
              business_address: venue.business_address || "",
              venue_name: venue.venue_name || "",
              venue_address: venue.venue_address || "",
              capacity: venue.capacity || "",
              additional_facilities: venue.additional_facilities || "",
              signatory_name: venue.signatory_name || "",
              signatory_designation: venue.signatory_designation || "",
              signatory_email: venue.signatory_email || "",
              signatory_phone: venue.signatory_phone || ""
            });
            setGstRegistered(venue.gst_registered);
            setGstin(venue.gstin || "");
          }
        }
      } catch (err) {
        // No venue yet, stay on step 0
      }
    };
    checkExistingVenue();
  }, []);

  // Fetch agreement template
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await templateAPI.get();
        setAgreementContent(res.data.template.content);
      } catch (err) {
        // Use fallback
      }
    };
    fetchTemplate();
  }, []);

  // Determine business entity type from GSTIN 6th character
  const getBusinessType = useCallback(() => {
    if (!gstin || gstin.length < 6) return "Proprietorship";
    const sixthChar = gstin.charAt(5).toUpperCase();
    if (sixthChar === "C") return "Company";
    if (sixthChar === "F") {
      // If legal name contains "LLP", use LLP; otherwise Partnership
      const name = (venueData.legal_name || "").toUpperCase();
      return name.includes("LLP") ? "LLP" : "Partnership";
    }
    return "Proprietorship";
  }, [gstin, venueData.legal_name]);

  // Substitute template variables with venue data
  const getFilledAgreement = useCallback(() => {
    if (!agreementContent) return "";
    const today = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
    const dayNum = new Date().getDate();
    const monthName = new Date().toLocaleDateString("en-IN", { month: "long" });
    const businessType = getBusinessType();
    const venueAddr = venueData.venue_address || venueData.business_address || "[Venue Address]";
    const signatoryInfo = [venueData.signatory_name, venueData.signatory_designation].filter(Boolean).join(", ");
    return agreementContent
      // New template placeholders (Ver Final)
      .replace(/\[FULL LEGAL NAME OF VENUE PARTNER\]/g, venueData.legal_name || "[FULL LEGAL NAME OF VENUE PARTNER]")
      .replace(/\[Company \/ LLP \/ Partnership Firm \/ Proprietorship Concern\]/gi, businessType)
      .replace(/\[full address\]/g, venueData.business_address || "[full address]")
      .replace(/\[name and designation\]/g, signatoryInfo || "[name and designation]")
      .replace(/\[VENUE PARTNER NAME\]/g, venueData.legal_name || "[VENUE PARTNER NAME]")
      // Old template placeholders (backward compat)
      .replace(/\[Venue Partner Name\]/g, venueData.legal_name || "[Venue Partner Name]")
      .replace(/\[address\]/g, venueData.business_address || "[address]")
      .replace(/\[Venue Name\]/g, venueData.venue_name || venueData.trade_name || venueData.legal_name || "[Venue Name]")
      .replace(/\[Venue Address\]/g, venueAddr)
      .replace(/\[Effective Date\]/g, today)
      // Date placeholders: "[ ] day of [ ], 2026"
      .replace(/\[\s*\]\s*day of\s*\[\s*\]/g, `${dayNum} day of ${monthName}`)
      .replace(/\[●\]/g, today)
      .replace(/\[proprietorship \/ partnership \/ company\]/gi, businessType)
      .replace(/\(the address specified herein\)/gi, venueAddr)
      .replace(/\[Venue Partner \/Authorised Signatory Name\]/g, venueData.signatory_name || "[Authorised Signatory Name]")
      // Schedule A - Venue Details
      .replace(/Venue Name\nFull Address\nCapacity\nFacilities/g,
        `Venue Name: ${venueData.venue_name || venueData.trade_name || venueData.legal_name || ""}\nFull Address: ${venueAddr}\nCapacity: ${venueData.capacity || "N/A"}\nFacilities: ${venueData.additional_facilities || "N/A"}`)
      // Skynor Ventures signature block
      .replace(/FOR SKYNOR VENTURES PRIVATE LIMITED[\s\S]*?Date:\s*_+/, (match) =>
        match
          .replace(/Authorised Signatory:\s*_+/, "Authorised Signatory: s/d")
          .replace(/Signature:\s*_+/, "Signature: s/d")
          .replace(/Date:\s*_+/, `Date: ${today}`)
      )
      // Old-style Skynor block (backward compat)
      .replace(/For Skynor Ventures Private Limited[\s\S]*?Date:\s*_+/, (match) =>
        match
          .replace(/Signature:\s*_+/, "Signature: s/d")
          .replace(/Name:\s*_+/, "Name: MANIKANDAN A V ACA")
          .replace(/Title:\s*_+/, "Title: CHIEF FINANCIAL OFFICER")
          .replace(/Date:\s*_+/, `Date: ${today}`)
      )
      // Remove Venue Partner signature block (digital signature verification section replaces it)
      .replace(/FOR\s+(?:\[VENUE PARTNER NAME\]|.{1,80})\n\s*Venue Partner[\s\S]*?Date:\s*_+/g, "");
  }, [agreementContent, venueData, getBusinessType]);

  // Signature countdown
  useEffect(() => {
    if (signatureCountdown > 0) {
      const timer = setTimeout(() => setSignatureCountdown(signatureCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [signatureCountdown]);

  const handleScroll = useCallback(() => {
    const el = agreementRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
    if (atBottom) setHasScrolledToBottom(true);
  }, []);

  const handleGSTVerify = async () => {
    if (gstin.length !== 15) {
      toast.error("GSTIN must be 15 characters");
      return;
    }
    setGstLoading(true);
    try {
      const res = await gstAPI.verify(gstin);
      const data = res.data.data;
      setVenueData(prev => ({
        ...prev,
        legal_name: data.legal_name || prev.legal_name,
        trade_name: data.trade_name || prev.trade_name,
        business_address: data.address || prev.business_address,
        venue_name: data.trade_name || data.legal_name || prev.venue_name,
        venue_address: data.address || prev.venue_address,
      }));
      toast.success("GST details fetched successfully");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "";
      if (status === 402) {
        toast.error("GST API needs activation. Please enter details manually.", { duration: 6000 });
      } else {
        toast.error(detail || "GST verification failed. Please enter details manually.");
      }
    } finally {
      setGstLoading(false);
    }
  };

  const handleSaveVenue = async () => {
    if (!venueData.legal_name || !venueData.business_address || !venueData.signatory_name || !venueData.signatory_designation || !venueData.signatory_email) {
      toast.error("Please fill all required fields including email");
      return;
    }
    setLoading(true);
    try {
      // Auto-fill venue name/address from business details if still empty
      const dataToSave = {
        ...venueData,
        venue_name: venueData.venue_name || venueData.trade_name || venueData.legal_name,
        venue_address: venueData.venue_address || venueData.business_address,
      };
      await venueAPI.create({
        gst_registered: gstRegistered,
        gstin: gstin || null,
        ...dataToSave
      });
      setVenueData(dataToSave);
      setCurrentStep(1);
      toast.success("Details saved! Please review the agreement.");
    } catch (err) {
      toast.error("Failed to save venue details");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAgreement = async () => {
    setLoading(true);
    try {
      await venueAPI.submit();
      // Request signature OTP
      await authAPI.requestSignatureOTP(user.phone || venueData.signatory_phone);
      setShowSignatureDialog(true);
      setSignatureCountdown(60);
      toast.success("Signature OTP sent to your phone");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit agreement");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySignature = async () => {
    if (signatureOtp.length !== 4) return;
    setSignatureLoading(true);
    try {
      await authAPI.verifySignatureOTP(user.phone || venueData.signatory_phone, signatureOtp);
      const execRes = await venueAPI.execute();
      setExecutionTimestamp(execRes.data.timestamp);
      setShowSignatureDialog(false);
      setCurrentStep(2);
      toast.success("Agreement executed successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signature verification failed");
      setSignatureOtp("");
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleResendSignatureOTP = async () => {
    if (signatureCountdown > 0) return;
    try {
      await authAPI.requestSignatureOTP(user.phone || venueData.signatory_phone);
      setSignatureCountdown(60);
      setSignatureOtp("");
      toast.success("OTP resent");
    } catch (err) {
      toast.error("Failed to resend OTP");
    }
  };

  useEffect(() => {
    if (signatureOtp.length === 4 && showSignatureDialog) {
      handleVerifySignature();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureOtp]);

  const handleDownloadPDF = async () => {
    try {
      const filledText = getFilledAgreement();
      const doc = await generateAgreementPDF({
        venueData,
        gstin,
        filledAgreementText: filledText,
        executionTimestamp,
      });
      doc.save(`BookMyMandap_Agreement_${venueData.legal_name || "Venue"}.pdf`);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("PDF generation error:", err);
      toast.error("Failed to generate PDF");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("bmm_token");
    localStorage.removeItem("bmm_user");
    navigate("/");
  };

  const renderField = (label, field, required = false, type = "text") => (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-[#991B1B]">*</span>}
      </Label>
      <Input
        data-testid={`venue-${field}-input`}
        type={type}
        value={venueData[field]}
        onChange={(e) => {
          const val = e.target.value;
          setVenueData(prev => {
            const updated = { ...prev, [field]: val };
            // Auto-sync venue fields from business fields if venue fields are empty or match previous value
            if (field === "trade_name" && (!prev.venue_name || prev.venue_name === prev.trade_name)) {
              updated.venue_name = val;
            }
            if (field === "legal_name" && (!prev.venue_name || prev.venue_name === prev.legal_name)) {
              updated.venue_name = val;
            }
            if (field === "business_address" && (!prev.venue_address || prev.venue_address === prev.business_address)) {
              updated.venue_address = val;
            }
            return updated;
          });
        }}
        className="bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#991B1B]/20 focus:border-[#991B1B]"
        placeholder={`Enter ${label.toLowerCase()}`}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BookMyMandap" className="h-9 object-contain" />
            <div className="h-6 w-px bg-slate-300"></div>
            <span className="text-sm font-semibold text-[#991B1B]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Venue Onboarding Portal</span>
          </div>
          <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={handleLogout} className="text-slate-500 hover:text-slate-700">
            <LogOut className="h-4 w-4 mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      {/* Progress steps */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((step, idx) => (
            <div key={step.id} className="flex items-center flex-1">
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                idx === currentStep ? "bg-[#991B1B] text-white shadow-md shadow-red-900/20" :
                idx < currentStep ? "bg-green-50 text-green-700 border border-green-200" :
                "bg-slate-100 text-slate-400"
              }`}>
                <step.icon className="h-4 w-4" />
                <span className="text-sm font-medium hidden sm:block">{step.label}</span>
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${idx < currentStep ? "bg-green-300" : "bg-slate-200"}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 0: GST Details */}
        {currentStep === 0 && (
          <Card className="border-slate-200 shadow-xl shadow-slate-200/50 max-w-xl mx-auto">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Business Details
              </CardTitle>
              <CardDescription>Tell us about your venue business</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* GST question */}
              {gstRegistered === null && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-slate-700">Are you GST registered?</Label>
                  <div className="flex gap-3">
                    <Button
                      data-testid="gst-yes-btn"
                      onClick={() => setGstRegistered(true)}
                      variant="outline"
                      className="flex-1 h-12 border-slate-200 hover:bg-[#991B1B]/5 hover:border-[#991B1B]/30 hover:text-[#991B1B]"
                    >
                      Yes, GST Registered
                    </Button>
                    <Button
                      data-testid="gst-no-btn"
                      onClick={() => setGstRegistered(false)}
                      variant="outline"
                      className="flex-1 h-12 border-slate-200 hover:bg-slate-50"
                    >
                      No
                    </Button>
                  </div>
                </div>
              )}

              {/* GST Registered - GSTIN Input */}
              {gstRegistered === true && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-slate-700">GSTIN <span className="text-[#991B1B]">*</span></Label>
                    <button onClick={() => { setGstRegistered(null); setGstin(""); }} className="text-xs text-slate-400 hover:text-slate-600">Change</button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      data-testid="gstin-input"
                      value={gstin}
                      onChange={(e) => setGstin(e.target.value.toUpperCase().slice(0, 15))}
                      placeholder="Enter 15-digit GSTIN"
                      className="bg-slate-50 border-slate-200 focus:ring-2 focus:ring-[#991B1B]/20 focus:border-[#991B1B] uppercase font-mono"
                      maxLength={15}
                    />
                    <Button
                      data-testid="gst-verify-btn"
                      onClick={handleGSTVerify}
                      disabled={gstin.length !== 15 || gstLoading}
                      className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] whitespace-nowrap"
                    >
                      {gstLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Verify</>}
                    </Button>
                  </div>
                </div>
              )}

              {/* Manual form / auto-populated */}
              {gstRegistered !== null && (
                <div className="space-y-4">
                  {gstRegistered === false && (
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-slate-600">Enter your business details manually</Label>
                      <button onClick={() => setGstRegistered(null)} className="text-xs text-slate-400 hover:text-slate-600">Change</button>
                    </div>
                  )}
                  {renderField("Legal / Business Name", "legal_name", true)}
                  {renderField("Trade Name", "trade_name")}
                  {renderField("Registered Business Address", "business_address", true)}

                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Venue Details <span className="normal-case font-normal tracking-normal text-slate-500">(Please edit if different from registered address)</span></p>
                    {renderField("Venue Name", "venue_name")}
                    {renderField("Venue Address", "venue_address")}
                    {renderField("Capacity (e.g. 500 guests)", "capacity")}
                    {renderField("Additional Facilities", "additional_facilities")}
                  </div>

                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-3">Authorized Signatory</p>
                    {renderField("Full Name", "signatory_name", true)}
                    {renderField("Designation", "signatory_designation", true)}
                    {renderField("Email", "signatory_email", true, "email")}
                    {renderField("Phone", "signatory_phone")}
                  </div>

                  <Button
                    data-testid="save-venue-btn"
                    onClick={handleSaveVenue}
                    disabled={loading}
                    className="w-full h-12 bg-[#991B1B] text-white hover:bg-[#7F1D1D] hover:-translate-y-0.5 transition-all duration-200 shadow-md shadow-red-900/20 font-medium"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Continue to Agreement
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 1: Agreement Preview */}
        {currentStep === 1 && (
          <Card className="border-slate-200 shadow-xl shadow-slate-200/50 max-w-3xl mx-auto">
            <CardHeader>
              <CardTitle className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Partner Agreement
              </CardTitle>
              <CardDescription>Please read the entire agreement before accepting</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                ref={agreementRef}
                onScroll={handleScroll}
                data-testid="agreement-preview"
                className="agreement-scroll h-[55vh] overflow-y-auto border border-slate-200 rounded-lg p-6 bg-slate-50 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
                style={{ fontFamily: "'Satoshi', sans-serif" }}
              >
                {getFilledAgreement() || "Loading agreement..."}
              </div>

              {!hasScrolledToBottom && (
                <p className="text-xs text-center text-amber-600 font-medium">
                  Please scroll to the bottom of the agreement to enable acceptance
                </p>
              )}

              <div className="flex gap-3">
                <Button
                  data-testid="back-to-details-btn"
                  variant="outline"
                  onClick={() => setCurrentStep(0)}
                  className="border-slate-200"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
                </Button>
                <Button
                  data-testid="accept-agreement-btn"
                  onClick={handleAcceptAgreement}
                  disabled={!hasScrolledToBottom || loading}
                  className={`flex-1 h-12 font-medium transition-all duration-200 ${
                    hasScrolledToBottom
                      ? "bg-[#991B1B] text-white hover:bg-[#7F1D1D] hover:-translate-y-0.5 shadow-md shadow-red-900/20"
                      : "bg-slate-100 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {hasScrolledToBottom ? "Accept & Sign Digitally" : "Read agreement to continue"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Complete */}
        {currentStep === 2 && (
          <Card className="border-slate-200 shadow-xl shadow-slate-200/50 max-w-xl mx-auto text-center">
            <CardContent className="pt-10 pb-8 space-y-6">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Agreement Executed
                </h2>
                <p className="text-sm text-slate-500">
                  Your venue partner agreement has been digitally signed and executed.
                </p>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2 text-sm">
                <p className="text-slate-600"><span className="font-medium text-slate-800">Partner:</span> {venueData.legal_name}</p>
                {venueData.venue_name && <p className="text-slate-600"><span className="font-medium text-slate-800">Venue:</span> {venueData.venue_name}</p>}
                <p className="text-slate-600"><span className="font-medium text-slate-800">Signatory:</span> {venueData.signatory_name}{venueData.signatory_designation ? `, ${venueData.signatory_designation}` : ""}</p>
                {executionTimestamp && (
                  <p className="text-slate-600">
                    <span className="font-medium text-slate-800">Signed:</span>{" "}
                    {new Date(executionTimestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST
                  </p>
                )}
              </div>

              <Button
                data-testid="download-pdf-btn"
                onClick={handleDownloadPDF}
                className="h-12 px-8 bg-[#991B1B] text-white hover:bg-[#7F1D1D] hover:-translate-y-0.5 transition-all duration-200 shadow-md shadow-red-900/20 font-medium"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Agreement PDF
              </Button>

              <Button
                data-testid="proceed-venue-onboarding-btn"
                onClick={() => window.open("https://bookmymandap.in/admin", "_blank")}
                className="h-12 px-8 bg-purple-600 text-white hover:bg-purple-700 hover:-translate-y-0.5 transition-all duration-200 shadow-md shadow-purple-600/20 font-medium"
              >
                Proceed for Venue Onboarding
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Signature OTP Dialog */}
      <Dialog open={showSignatureDialog} onOpenChange={setShowSignatureDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Digital Signature Verification
            </DialogTitle>
            <DialogDescription>
              Enter the OTP sent to +91 {user.phone || venueData.signatory_phone} to digitally sign this agreement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <InputOTP
                data-testid="signature-otp-input"
                maxLength={4}
                value={signatureOtp}
                onChange={setSignatureOtp}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                  <InputOTPSlot index={1} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                  <InputOTPSlot index={2} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                  <InputOTPSlot index={3} className="w-14 h-16 text-xl font-bold border-slate-300 bg-white" />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {signatureLoading && (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                <ShieldCheck className="h-4 w-4 animate-pulse" /> Verifying signature...
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <button
                data-testid="signature-resend-otp-btn"
                onClick={handleResendSignatureOTP}
                disabled={signatureCountdown > 0}
                className="text-[#991B1B] hover:text-[#7F1D1D] font-medium disabled:text-slate-400 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <RotateCcw className="h-3.5 w-3.5" /> Resend OTP
              </button>
              {signatureCountdown > 0 && <span className="text-slate-400">{signatureCountdown}s</span>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
